/**
 * Minute markets module handles all the interactions
 * with the minute market component.
 *
 * The basic workflow to work with this component is:
 *     -init (providing the event id and the language)
 *     -bindLaunchButton: (providing the query selector for the UI element to be bound with show action)
 *     -show
 *     -hide
 *     -destroy: release all the internal dependencies and remove the element from the application, also
 *               remove the event handler for the UI set as the one to open the minute markets component
 *
 * @module app/components/MinuteMarkets
 *
 */
import DeviceDetector from './DeviceDetector';
import MessageBusTopics from '../../common/MessageBusTopics';
import { hidePagePreloader, showPagePreloader } from '../libs/PagePreloaderHelper';

/**
 * The current event identifier to work with the minute markets.
 */
var currentEventId;

/**
 * The current locale of the application.
 * This information is required by the minute market component to ensure
 * both are working in with the same locale.
 */
var currentLocale;

/**
 * The launch button DOM reference to show the minute market component.
 */
var $launchButton;

/**
 * The jQuery wrapped entry point reference to mount the minute markets splash screen.
 */
var $entryPoint;

/**
 * The application configuration object.
 */
var config;

/**
 * Local ref to the window messagebus object
 *
 * @type {Object}
 *
 * @private
 */
var messageBus;

/**
 * Local ref to the window appendScriptTag object
 *
 * @type {Function}
 *
 * @private
 */
var appendScriptTag;

/**
 * Initialization for the local references
 *
 * @private
 */
var initLocals = function initLocals() {

    config = window.whStatic.config;
    messageBus = window.WH.messageBus;
    appendScriptTag = window.appendScriptTag;
};

/**
 * Setup the component to work against a concrete event and locale
 *
 * @param {string} eventId           Event identifier to setup in the minute market component
 * @param {string} locale            Current locale for the application
 * @param {string} deviceType        The device type the application is running on
 *                                   {"desktop", "mobile", "tablet", "native"}
 * @param {jQuery el} entryPoint     jQuery-wrapped entry point element
 *
 *
 * @returns {void}
 *
 * @public
 */
var doInit = function doInit(eventId, locale, entryPoint) {

    currentEventId = eventId;
    currentLocale = locale;
    $entryPoint = entryPoint;

    ensureScriptTag();
};

/**
 * Ensures we have the minute markets script tag in the DOM
 *
 * @returns {void}
 *
 * @private
 */
var ensureScriptTag = function ensureScriptTag() {

    var mmjsUrl = composeMinuteMarketScriptURL();
    if (!document.querySelector('script[src="' + mmjsUrl + '"]')) {

        appendScriptTag(mmjsUrl, true);
    }
};

/**
 * Returns minute markets script URL
 *
 * @returns {string}
 *
 * @private
 */
var composeMinuteMarketScriptURL = function composeMinuteMarketScriptURL() {

    return config.minuteMarkets.host + config.minuteMarkets.path;
};

/**
 * Asks MM application to mount entry point within a container of a given id
 *
 * @returns {void}
 *
 * @public
 */
var requestEntryPointMount = function requestEntryPointMount() {

    showPagePreloader();

    messageBus.publishSticky(MessageBusTopics.MINUTE_MARKETS_MOUNT_ENTRY_POINT, {
        nodeId: $entryPoint.attr('id'),
        locale: currentLocale,
    });

    messageBus.subscribe(MessageBusTopics.MINUTE_MARKETS_ENTRY_POINT_MOUNTED, onEntryPointMounted);
};

/**
 * Callback executed when ENTRY_POINT_MOUNTED event is published by the MM app
 *
 * @param {string} topic message bus topic
 * @param {Object} data object which contains button selector, so an event listener can be attached to it
 *
 * @returns {void}
 *
 * @public
 */
var onEntryPointMounted = function onEntryPointMounted(topic, data) {

    if (data && data.launchButtonSelector) {

        $entryPoint.data('is-mounted', true);
        bindLaunchButton(data.launchButtonSelector);
        showEntryPoint();
    }

    hidePagePreloader();
};

/**
 * Shows the minute markets component
 *
 * @returns {void}
 *
 * @public
 */
var show = function show() {

    messageBus.publish(MessageBusTopics.MINUTE_MARKETS_SHOW, {
        eventId: currentEventId,
        locale: currentLocale,
        device: DeviceDetector.getResult(),
    });
};


/**
 * Hides the minute markets component
 *
 * @returns {void}
 *
 * @public
 */
var hide = function hide() {

    if (isFeatureEnabled()) {
        messageBus.publish(MessageBusTopics.MINUTE_MARKETS_HIDE);
    }
};

/**
 * Destroys the minute markets component and all the
 * related subscriptions
 *
 * @returns {void}
 *
 * @public
 */
var destroy = function destroy() {

    if (isFeatureEnabled()) {

        messageBus.publish(MessageBusTopics.MINUTE_MARKETS_DESTROY);
        unbindLaunchButton();
    }
};

/**
 * Creates the click event relation with the showMinuteMarkets functionality
 *
 * @param querySelector            Selector (valid for the query selector) to pickup the
 *                                 DOM element to be attached to the click to open the
 *                                 minute market component
 *
 * @returns {void}
 *
 * @public
 */
var bindLaunchButton = function bindLaunchButton(querySelector) {

    $launchButton = document.querySelector(querySelector);

    if ($launchButton) {

        $launchButton.addEventListener('click', show, false);
    }
};

/**
 * Removes the click event relation with the showMinuteMarkets functionality
 *
 * @returns {void}
 *
 * @private
 */
var unbindLaunchButton = function unbindLaunchButton() {

    if ($launchButton) {

        $launchButton.removeEventListener('click', show, false);
    }
};


/**
 * Gets if the minute market feature is enable
 *
 * @returns {boolean}    Returns true if the minute market feature is enabled
 *                       in the configurations, false otherwise
 *
 * @private
 *
 */
var isFeatureEnabled = function isFeatureEnabled() {

    return !!(config.minuteMarkets && config.minuteMarkets.enabled);
};

/**
 * Returns an array of collections which are minute markets as defined in the config
 *
 * @returns {Array}
 *
 * @private
 */
var getMinuteMarketCollections = function getMinuteMarketCollections() {

    return config.minuteMarkets.collections || [];
};

/**
 * Checks if a collection belongs to minute markets
 *
 * @param   {string} collectionName name of the collection
 *
 * @returns {boolean}
 *
 * @private
 */
var isMinuteMarketsCollection = function isMinuteMarketsCollection(collectionName) {

    if (!collectionName || !isFeatureEnabled()) {
        return false;
    }

    var mmCollections = getMinuteMarketCollections();
    return mmCollections.indexOf(collectionName) > -1;
};

/**
 * After minute markets collection is clicked this function gets called to ask Minute Market app to mount the
 * entry point splash screens, so the user can click on a button to start the application
 *
 * @returns {void}
 *
 * @private
 */
var mountEntryPoint = function mountEntryPoint() {

    if (isFeatureEnabled()) {
        if ($entryPoint.data('is-mounted')) {

            return showEntryPoint();
        }

        requestEntryPointMount();
    }
};

/**
 * Function to display the entry point container
 *
 * @returns {void}
 *
 * @private
 */
var showEntryPoint = function showEntryPoint() {

    if (isFeatureEnabled()) {

        $entryPoint.removeClass('displayNone');
    }
};

/**
 * Function to hide the entry point container
 *
 * @returns {void}
 *
 * @private
 */
var hideEntryPoint = function hideEntryPoint() {

    if (isFeatureEnabled()) {

        $entryPoint.addClass('displayNone');
    }
};

/**
 * Complete the required logic to use the minute markets component
 * for the current event.
 *
 * This logic is only working if the flag in the configuration is set the
 * enable flag to true.
 *
 * @returns {void}
 *
 * @public
 */
var init = function init(sportsbookConfig, entryPoint) {

    initLocals();

    if (isFeatureEnabled()) {

        doInit(sportsbookConfig.eventId, sportsbookConfig.lang, entryPoint);
    }
};

/**
 * Public variables and functions
 *
 * @type {Object}
 *
 * @public
 */
var api = {
    init: init,
    show: show,
    hide: hide,
    destroy: destroy,
    mountEntryPoint: mountEntryPoint,
    hideEntryPoint: hideEntryPoint,
    isMinuteMarketsCollection: isMinuteMarketsCollection,
};

export default api;
